import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useYouTube } from '../context/YouTubeContext';
import { useEvents } from '../context/EventsContext';
import usePullToRefresh from '../hooks/usePullToRefresh';
import useInternalLinkNav from '../hooks/useInternalLinkNav';
import { API_BASE } from '../utils/apiBase';
import BottomNav from '../components/BottomNav';
import DGNavigation from '../components/navigation/DGNavigation';
import VisitorLoginButton from '../components/VisitorLoginButton';
import { HOME_SECTIONS, DEFAULT_HOME_ORDER } from '../components/home/homeSections';
import { renderHomeContent } from '../components/home/homeRenderer';

/**
 * HomeScreen — écran d'accueil de l'app.
 *
 * L'accueil est composé de SECTIONS autonomes (components/home/sections/).
 *  - Si une page Puck `accueil` est PUBLIÉE (GET /api/pages/accueil), on rend
 *    SES blocs (sections dynamiques + blocs de contenu) → l'accueil devient
 *    éditable par le CMS.
 *  - Sinon, repli sur l'ordre par défaut (DEFAULT_HOME_ORDER) = l'accueil
 *    historique. Le repli s'affiche AUSSI pendant le chargement → accueil
 *    instantané, jamais d'écran vide.
 */

// Cache de session (module-level) : évite de refetcher/reflasher à chaque
// remontée de l'accueil. undefined = pas encore chargé.
const _accueilCache = { content: undefined };

const HomeScreen = () => {
  const { isAuthenticated, activeRole } = useAuth();
  const { isDark } = useTheme();

  // Pull-to-refresh : rafraîchit les données partagées (YouTube + événements).
  const { fetchYouTubeData } = useYouTube();
  const { fetchEvents } = useEvents();
  const handleRefresh = useCallback(async () => {
    await Promise.all([fetchYouTubeData(), fetchEvents(null, true)]);
  }, [fetchYouTubeData, fetchEvents]);
  const { pullToRefreshProps, PullToRefreshIndicator } = usePullToRefresh(handleRefresh);

  // Page Puck `accueil` (si publiée). null = pas de page → repli.
  const [accueilContent, setAccueilContent] = useState(_accueilCache.content);
  useEffect(() => {
    if (_accueilCache.content !== undefined) return; // déjà résolu cette session
    let alive = true;
    (async () => {
      let value = null;
      try {
        const res = await fetch(`${API_BASE}/api/pages/accueil`, { headers: { Accept: 'application/json' } });
        if (res.ok) {
          const page = await res.json();
          const content = page?.puckData?.content;
          if (Array.isArray(content) && content.length > 0) value = content;
        }
      } catch (_) { /* repli sur l'accueil par défaut */ }
      _accueilCache.content = value;
      if (alive) setAccueilContent(value);
    })();
    return () => { alive = false; };
  }, []);

  const isDgAdmin = activeRole === 'dg' || activeRole === 'admin';
  const usePuckAccueil = Array.isArray(accueilContent) && accueilContent.length > 0;
  // Navigation interne pour les liens des blocs CMS (Carte/Bouton → /page/…).
  const onCmsLinkClick = useInternalLinkNav();

  return (
    <div
      className={`min-h-screen pb-20 transition-colors duration-200 bg-pcc-bg ${isDgAdmin ? 'md:pl-64' : ''}`}
      data-testid="home-screen"
      {...pullToRefreshProps}
    >
      <PullToRefreshIndicator />

      {!isAuthenticated && (
        <div className="fixed right-4 z-50 pcc-below-status-bar">
          <VisitorLoginButton variant="default" />
        </div>
      )}

      {usePuckAccueil
        ? <div onClick={onCmsLinkClick}>{renderHomeContent(accueilContent, isDark ? 'dark' : 'light', { isMember: isAuthenticated && activeRole !== 'visitor' })}</div>
        : DEFAULT_HOME_ORDER.map((key) => {
            const entry = HOME_SECTIONS[key];
            if (!entry) return null;
            const Section = entry.Component;
            return <Section key={key} />;
          })}

      {isDgAdmin ? <DGNavigation /> : <BottomNav />}
    </div>
  );
};

export default HomeScreen;
